import type { InboxAttachment, InboxReaction } from '@unipostudio/platform-constants';
import type { InboxConversationPage, InboxStats, InboxView } from '@unipostudio/shared-types';
import { api } from './api';

/**
 * UI-facing inbox event shape. We normalize the backend's raw
 * `InboxEvent` (Prisma) into this — the backend returns `text` /
 * `receivedAt` / `fromPlatformId` / uppercase platform + type enums,
 * but the UI was built against lowercase platform names + friendly
 * field names. We map at the service layer so component code stays
 * stable even if the storage shape shifts.
 */
export interface InboxEvent {
  id: string;
  platform: string; // 'instagram' / 'facebook' / ... (lowercased for icon lookup)
  type: 'comment' | 'dm' | 'mention' | 'story_reply' | 'reaction' | 'review';
  fromName: string;
  /** Backend has no separate "handle" — we surface `fromPlatformId` here
   *  so the UI's @handle slot has something useful (display name from
   *  `fromName` is the primary label). */
  fromHandle: string;
  fromAvatarUrl: string | null;
  /** Display copy: maps from `text` on the backend, which may be null
   *  for reactions or media-only DMs. */
  message: string;
  postId: string | null;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  isReplied: boolean;
  /** ISO 8601 timestamp — sourced from `receivedAt` on the backend. */
  createdAt: string;
  /** Conversation grouping key (DMs only). Null for comments/mentions. */
  conversationId: string | null;
  /** INBOUND (from user) vs OUTBOUND (our sent reply). Used by the threaded
   *  view to render bubbles on the appropriate side. */
  direction: 'INBOUND' | 'OUTBOUND';
  replies?: InboxReply[];
  /** The customer sent a photo, video or file the server stored; GET /inbox/:id/media signs a link to it. */
  hasMedia?: boolean;
  /** Its MIME type, when known (image/jpeg, video/mp4…). */
  mediaMimeType?: string | null;
  /** What the customer sent besides text, as the server normalised it (photos, shared posts, stories…). */
  attachments?: InboxAttachment[];
  /** Reactions left on the message (Instagram ❤️…). */
  reactions?: InboxReaction[];
}

export interface InboxReply {
  id: string;
  text: string;
  sentBy: string;
  sentAt: string;
  status: string;
  /** The platform's error for a reply it did not deliver, e.g. "131047: Re-engagement message" (WhatsApp). */
  deliveryError?: string | null;
  /** A photo or video sent with the reply. */
  attachment?: { url: string; kind: 'image' | 'video'; mimeType: string } | null;
}

export interface InboxReplyDto {
  eventId: string;
  message: string;
  /**
   * An approved WhatsApp template to send in place of the text.
   *
   * Only set once Meta's 24-hour window has shut, when a freeform reply is
   * rejected outright. `message` still carries the template body so the
   * thread shows what the customer received.
   */
  template?: ReplyTemplateChoice;
  /** One UUID per reply: the same key sent again never sends a second reply. */
  idempotencyKey?: string;
  /** A media library photo or video sent with a DM reply. */
  mediaId?: string;
}

/** A template the person picked, with the values they entered (`body.1`, `header.name`, `button.0`, `header.media`…). */
export interface ReplyTemplateChoice {
  name: string;
  language: string;
  values?: Record<string, string>;
}

/** One value a template needs: a `{{1}}` / `{{name}}` placeholder, or a URL button's suffix (key = button index). */
export interface TemplatePlaceholderInfo {
  key: string;
  component: 'header' | 'body' | 'button';
}

/** What an approved WhatsApp template looks like to the composer. */
export interface WhatsAppTemplate {
  name: string;
  language: string;
  category: string;
  header?: string | null;
  body: string;
  placeholders?: TemplatePlaceholderInfo[];
  /** IMAGE, VIDEO or DOCUMENT: the header is media, sent as an https link (`values['header.media']`). */
  mediaHeader?: 'IMAGE' | 'VIDEO' | 'DOCUMENT' | null;
  /** False: needs a location header, a coupon code or a one-time password, so it cannot be sent from the inbox. */
  supported?: boolean;
  /** Why this customer may not receive it. */
  refusal?: 'opted_out' | 'no_marketing_opt_in' | null;
}

/** Messaging consent on the customer record (WhatsApp). */
export interface CustomerConsent {
  optedOut: boolean;
  marketingOptInAt: string | null;
  marketingOptInSource: string | null;
}

export interface WhatsAppWindowInfo {
  platform: string;
  window: { state: 'open' | 'closing' | 'closed'; msRemaining: number; closesAt: string; canSendFreeform: boolean } | null;
  templates: WhatsAppTemplate[];
  customer?: CustomerConsent | null;
}

/** Whether a reply can go out now, for any channel. */
export interface ReplyWindowInfo extends WhatsAppWindowInfo {
  replyWindow: {
    windowed: boolean;
    canSendFreeform: boolean;
    msRemaining: number | null;
    closesAt: string | null;
    humanAgentClosesAt: string | null;
  } | null;
  humanAgentTagAvailable: boolean;
  canReply: boolean;
  refusal: 'window_closed' | 'x_not_replyable' | null;
}

export interface InboxListParams {
  page?: number;
  limit?: number;
  platform?: string;
  type?: string;
  isRead?: boolean;
  isArchived?: boolean;
  search?: string;
  dateFrom?: string; // ISO datetime, inclusive
  dateTo?: string;
  /** One conversation only (its key): the whole thread, opened from the list. */
  conversation?: string;
}

/** One page of the inbox list. */
export interface InboxPage {
  data: InboxEvent[];
  pagination: { total: number; unreadCount: number; page: number; hasMore: boolean };
}

interface BackendInboxEvent {
  id: string;
  platform: string;            // uppercase: 'INSTAGRAM' / 'FACEBOOK' / ...
  type: string;                // uppercase: 'COMMENT' / 'DM' / 'MENTION' / 'STORY_REPLY' / 'REACTION' / 'REVIEW'
  conversationId: string | null;
  direction: 'INBOUND' | 'OUTBOUND';
  fromPlatformId: string | null;
  fromName: string | null;
  fromAvatarUrl: string | null;
  text: string | null;
  postResultId: string | null;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  isReplied: boolean;
  receivedAt: string;
  rawPayload?: Record<string, unknown> | null;
  replies?: Array<{
    id: string;
    text: string;
    sentBy: string;
    sentAt: string;
    status: string;
    platformReplyId?: string | null;
    deliveryError?: string | null;
    attachment?: { url: string; kind: 'image' | 'video'; mimeType: string } | null;
  }>;
}

interface BackendInboxListResponse {
  data: BackendInboxEvent[];
  total: number;
  unreadCount: number;
}

/** Backend uses UPPERCASE platform names — the UI keys icons + display
 *  off lowercase strings. Tiny lookup. */
function toFrontPlatform(p: string): string {
  // GOOGLE_BUSINESS keeps its underscore: the icon id is google_business.
  return p.toLowerCase();
}

/** Backend uses UPPERCASE InboxType enum — UI uses snake_case strings. */
function toFrontType(t: string): InboxEvent['type'] {
  switch (t) {
    case 'COMMENT':     return 'comment';
    case 'DM':          return 'dm';
    case 'MENTION':     return 'mention';
    case 'STORY_REPLY': return 'story_reply';
    case 'REACTION':    return 'reaction';
    case 'REVIEW':      return 'review';
    default:            return 'comment';
  }
}

function mapEvent(raw: BackendInboxEvent): InboxEvent {
  const payload = raw.rawPayload ?? null;
  const mediaKey = payload && typeof payload['mediaS3Key'] === 'string' ? payload['mediaS3Key'] : null;
  const mediaMime = payload && typeof payload['mediaMimeType'] === 'string' ? payload['mediaMimeType'] : null;
  return {
    hasMedia: mediaKey !== null,
    mediaMimeType: mediaMime,
    ...(payload && Array.isArray(payload['attachments']) ? { attachments: payload['attachments'] as unknown as InboxAttachment[] } : {}),
    ...(payload && Array.isArray(payload['reactions']) ? { reactions: payload['reactions'] as unknown as InboxReaction[] } : {}),
    id: raw.id,
    platform: toFrontPlatform(raw.platform),
    type: toFrontType(raw.type),
    fromName: raw.fromName ?? 'Unknown sender',
    // No dedicated handle column on the backend — use fromPlatformId as
    // the closest equivalent (looks like "@user.handle" on most platforms,
    // numeric on others). UI gracefully renders whatever's there.
    fromHandle: raw.fromPlatformId ?? '',
    fromAvatarUrl: raw.fromAvatarUrl,
    message: raw.text ?? '',
    postId: raw.postResultId,
    isRead: raw.isRead,
    isStarred: raw.isStarred,
    isArchived: raw.isArchived,
    isReplied: raw.isReplied,
    createdAt: raw.receivedAt,
    conversationId: raw.conversationId,
    direction: raw.direction ?? 'INBOUND',
    replies: raw.replies?.map((r) => ({
      id: r.id,
      text: r.text,
      sentBy: r.sentBy,
      sentAt: r.sentAt,
      status: r.status,
      deliveryError: r.deliveryError ?? null,
      attachment: r.attachment ?? null,
    })),
  };
}

/** What GET /inbox/conversations is asked: one view or all, and the filters, a page at a time. */
export interface ConversationListParams {
  view?: InboxView | null;
  /** SCREAMING_CASE platform, as the API enum. */
  platform?: string | null;
  q?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  memberId?: string | null;
  levelId?: string | null;
  /** One customer's conversations (the customer drawer). */
  customerKey?: string | null;
  cursor?: string | null;
  limit?: number;
}

/** Only the parameters that are set, as the query string carries them. */
function conversationQuery(params: ConversationListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) if (v !== null && v !== undefined && v !== '') out[k] = v as string | number;
  return out;
}

export const inboxService = {
  /** A page of whole conversations, with the counts of every view under the same filters. */
  async listConversations(params: ConversationListParams): Promise<InboxConversationPage<InboxEvent>> {
    const res = await api.get<InboxConversationPage<BackendInboxEvent>>('/inbox/conversations', { params: conversationQuery(params) });
    const body = res.data;
    return { ...body, data: (body?.data ?? []).map((row) => ({ ...row, events: row.events.map(mapEvent) })) };
  },

  /** How many conversations each view holds, and unread messages (the nav badge). */
  async conversationCounts(params: ConversationListParams = {}): Promise<{ counts: Record<InboxView, number>; unreadCount: number }> {
    const res = await api.get<{ counts: Record<InboxView, number>; unreadCount: number }>('/inbox/conversations/counts', { params: conversationQuery(params) });
    return res.data;
  },

  /** The stats strip over 7, 30 or 90 days, with the change against the period before. */
  async conversationStats(days: 7 | 30 | 90 = 7): Promise<InboxStats> {
    const res = await api.get<InboxStats>('/inbox/conversations/stats', { params: { days } });
    return res.data;
  },

  async list(params?: InboxListParams): Promise<InboxPage> {
    const res = await api.get<BackendInboxListResponse>('/inbox', { params });
    const body = res.data ?? { data: [], total: 0, unreadCount: 0 };
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 20;
    const total = body.total ?? 0;
    return {
      data: (body.data ?? []).map(mapEvent),
      pagination: { total, unreadCount: body.unreadCount ?? 0, page, hasMore: page * limit < total },
    };
  },

  async getById(id: string): Promise<InboxEvent> {
    const res = await api.get<BackendInboxEvent>(`/inbox/${id}`);
    return mapEvent(res.data);
  },

  async markRead(id: string): Promise<void> {
    await api.patch(`/inbox/${id}/read`);
  },

  async toggleStar(id: string): Promise<void> {
    await api.patch(`/inbox/${id}/star`);
  },

  async archive(id: string): Promise<void> {
    await api.patch(`/inbox/${id}/archive`);
  },

  /** Queues a reply; the id names the stored reply, so the agent's mark can point at it. */
  async reply(dto: InboxReplyDto): Promise<{ replyId: string | null }> {
    const res = await api.post<{ replyId?: string }>(`/inbox/${dto.eventId}/reply`, {
      text: dto.message,
      ...(dto.template ? { template: dto.template } : {}),
      ...(dto.idempotencyKey ? { idempotencyKey: dto.idempotencyKey } : {}),
      ...(dto.mediaId ? { mediaId: dto.mediaId } : {}),
    });
    return { replyId: res.data?.replyId ?? null };
  },

  /** Every message of a conversation, so a DM thread does not stay bold after it is read. */
  async markConversationRead(key: string): Promise<void> {
    await api.patch(`/inbox/conversations/${encodeURIComponent(key)}/read`);
  },

  async starConversation(key: string, starred: boolean): Promise<void> {
    await api.patch(`/inbox/conversations/${encodeURIComponent(key)}/star`, { starred });
  },

  /** Archives every message of a conversation, so it does not come straight back. */
  async archiveConversation(key: string): Promise<void> {
    await api.patch(`/inbox/conversations/${encodeURIComponent(key)}/archive`);
  },

  /** A five-minute signed link to the media a customer sent. */
  async mediaUrl(eventId: string): Promise<{ url: string; mimeType: string | null }> {
    const res = await api.get<{ url: string; mimeType: string | null }>(`/inbox/${eventId}/media`);
    return res.data;
  },

  /**
   * Which approved WhatsApp templates could reach this customer.
   *
   * Only worth asking once the window has shut: inside it a plain reply
   * works, and listing templates would invite paying for one nobody needs.
   * The window itself is computed in the browser from `lastInboundAt`, so
   * the banner appears without a round trip.
   */
  async whatsappWindow(eventId: string): Promise<WhatsAppWindowInfo> {
    const res = await api.get<WhatsAppWindowInfo>(`/inbox/${eventId}/whatsapp-window`);
    return res.data;
  },

  /**
   * Whether a reply can go out now, on any channel: Meta's 24-hour window on
   * Instagram and Messenger DMs (and the human-agent extension), X's rule
   * that only posts mentioning the account may be answered, and WhatsApp's
   * window with its templates and the customer's consent.
   */
  async replyWindow(eventId: string): Promise<ReplyWindowInfo> {
    const res = await api.get<ReplyWindowInfo>(`/inbox/${eventId}/reply-window`);
    return res.data;
  },

  /** Records the customer's marketing opt-in (with its source) or an opt-out. */
  async setCustomerConsent(
    eventId: string,
    input: { marketingOptIn?: boolean; source?: string; optedOut?: boolean },
  ): Promise<CustomerConsent> {
    const res = await api.patch<CustomerConsent>(`/inbox/${eventId}/customer-consent`, input);
    return res.data;
  },

  async syncNow(): Promise<{ enqueued: number; platforms: string[] }> {
    const res = await api.post<{ enqueued: number; platforms: string[] }>('/inbox/sync');
    return res.data ?? { enqueued: 0, platforms: [] };
  },

  async diagnostics(): Promise<InboxDiagnostics> {
    const res = await api.get<InboxDiagnostics>('/inbox/diagnostics');
    return res.data ?? { totalEvents: 0, perPlatform: [] };
  },

  async debugPoll(): Promise<InboxDebugResult> {
    const res = await api.post<InboxDebugResult>('/inbox/debug-poll');
    return res.data ?? { workspaceId: '', results: [] };
  },

  /**
   * Unreplied inbound events per day, bucketed in the given IANA timezone.
   * Feeds the calendar's "inbox load" footer line.
   */
  async dailyLoad(params: { from: string; to: string; tz: string }): Promise<InboxDailyLoad[]> {
    const res = await api.get<InboxDailyLoad[]>('/inbox/daily-load', { params });
    return res.data ?? [];
  },
};

export interface InboxDailyLoad {
  /** Local date, YYYY-MM-DD. */
  date: string;
  waiting: number;
}

export interface InboxDebugResult {
  workspaceId: string;
  results: InboxDebugProbe[];
}

export interface InboxDebugProbe {
  platform: string;
  endpoint: string;
  tokenUsed: 'page' | 'user' | 'apiKey' | 'missing';
  status: number | null;
  ok: boolean;
  itemCount?: number;
  summary: string;
  action: 'none' | 'reconnect' | 'app-review-required' | 'configure-server' | 'refresh-token-failed';
  hint?: string;
  rawBody: string;
}

export interface InboxDiagnostics {
  totalEvents: number;
  perPlatform: Array<{
    platform: string;
    connectState: string;
    syncMode: string;
    publishedPostsLast30d: number;
    inboxEventCount: number;
    /** Why, as a code; the empty state words it from the facts itself (`inbox.diag.*`). */
    reasonCode?: string;
    lastSync?: {
      at: string;
      outcome: 'success' | 'no-data' | 'error' | 'skipped';
      counts: Record<string, number>;
      error: string | null;
      durationMs: number;
    };
  }>;
}
